import { MAX_NESTING_LEVEL } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/constants';
import { JsonProperty } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/properties/types';
import { JsonSchema } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/types';

export const SchemaUtils = {
  generateJsonString: (schema: JsonSchema): string => {
    return JSON.stringify(schema, null, 2);
  },

  parseJsonString: (schemaString: string): JsonSchema | null => {
    try {
      const parsed = JSON.parse(schemaString);
      if (parsed.type === 'object' && parsed.properties) {
        return parsed as JsonSchema;
      }
      return null;
    } catch {
      return null;
    }
  },

  traverseSchemaForPaths: (properties: Record<string, JsonProperty>, currentPath: string[] = []): string[] => {
    const paths: string[] = [];

    Object.entries(properties).forEach(([key, property]) => {
      const fullPath = [...currentPath, key];
      const pathString = fullPath.join('.');

      if (property.type === 'string' || property.type === 'number' || property.type === 'boolean') {
        // Leaf node - add as variable path
        paths.push(pathString);
      } else if (property.type === 'object' && property.properties) {
        // Recursively traverse nested object
        paths.push(...SchemaUtils.traverseSchemaForPaths(property.properties, fullPath));
      } else if (property.type === 'array' && property.items) {
        // For arrays, we typically want the array itself as a path
        paths.push(pathString);

        // If array contains objects, also traverse those
        if (property.items.type === 'object' && property.items.properties) {
          paths.push(...SchemaUtils.traverseSchemaForPaths(property.items.properties, fullPath));
        }
      }
    });

    return paths;
  },

  extractVariablePaths: (schema: JsonSchema): string[] => {
    const paths = SchemaUtils.traverseSchemaForPaths(schema.properties);
    return paths.sort();
  },

  getVariableProperties: (schema: JsonSchema): Record<string, JsonProperty> => {
    const paths = SchemaUtils.extractVariablePaths(schema);
    const result: Record<string, JsonProperty> = {};

    paths.forEach((path) => {
      const property = SchemaUtils.getPropertyAtPath(schema, path.split('.'));
      if (property) {
        result[path] = property;
      }
    });

    return result;
  },

  generateExampleFromSchema: (schema: JsonSchema | JsonProperty): Record<string, unknown> | unknown => {
    if (!schema || typeof schema !== 'object') return {};

    if (schema.type === 'object' && 'properties' in schema && schema.properties) {
      const result: Record<string, unknown> = {};
      Object.entries(schema.properties).forEach(([key, propSchema]) => {
        result[key] = SchemaUtils.generateExampleFromProperty(propSchema);
      });
      return result;
    }

    return SchemaUtils.generateExampleFromProperty(schema as JsonProperty);
  },

  generateExampleFromProperty: (property: JsonProperty): unknown => {
    switch (property.type) {
      case 'object':
        if (property.properties) {
          const result: Record<string, unknown> = {};
          Object.entries(property.properties).forEach(([key, propSchema]) => {
            result[key] = SchemaUtils.generateExampleFromProperty(propSchema);
          });
          return result;
        }
        return {};

      case 'array':
        if (property.items) {
          return [SchemaUtils.generateExampleFromProperty(property.items)];
        }
        return ['example item'];

      case 'string':
        if (property.enum && property.enum.length > 0) {
          return property.enum[0];
        }
        return 'example text';

      case 'number':
        return 1;

      case 'boolean':
        return true;

      default:
        return 'example';
    }
  },

  getPropertyAtPath: (schema: JsonSchema, path: string[]): JsonProperty | null => {
    if (path.length === 0) return null;

    let current: JsonProperty | undefined = schema.properties[path[0]];

    for (let i = 1; i < path.length; i++) {
      const key = path[i];

      if (key === 'items') {
        if (current?.type !== 'array' || !current.items) return null;
        current = current.items;
      } else {
        if (current?.type !== 'object' || !current.properties) return null;
        current = current.properties[key];
      }
    }

    return current || null;
  },

  getParentAtPath: (schema: JsonSchema, path: string[]): { parent: JsonSchema | JsonProperty; key: string } | null => {
    if (path.length === 0) return null;

    if (path.length === 1) {
      return { parent: schema, key: path[0] };
    }

    const parentPath = path.slice(0, -1);
    const parentProperty = SchemaUtils.getPropertyAtPath(schema, parentPath);
    const key = path[path.length - 1];

    if (!parentProperty) return null;

    if (key === 'items') {
      if (parentProperty.type !== 'array') return null;
      return { parent: parentProperty, key };
    }

    if (parentProperty.type !== 'object' || !parentProperty.properties) return null;

    return { parent: parentProperty, key };
  }
};

export const ValidationUtils = {
  getNestedLevel: (path: string[]): number => {
    return path.length;
  },

  validateNestingLevel: (path: string[]): boolean => {
    return ValidationUtils.getNestedLevel(path) <= MAX_NESTING_LEVEL;
  }
};
